import React from 'react';
import { CollectionList } from 'types/interfaces/collectionList';
import { AssetUrls } from 'config/assets';

// Valid condition values that the backend accepts
export const VALID_CONDITIONS = ['NEAR_MINT', 'SLIGHTLY_PLAYED', 'MODERATELY_PLAYED', 'HEAVILY_PLAYED', 'DAMAGED'];

// Sort options for dropdown
export const SORT_OPTIONS = [
  { value: 'created_at-desc', label: 'Date Added' },
  { value: 'reference_price-desc', label: 'Price: High to Low' },
  { value: 'reference_price-asc', label: 'Price: Low to High' },
  { value: 'card_name-asc', label: 'Name: A-Z' },
  { value: 'card_name-desc', label: 'Name: Z-A' },
];

// View options for dropdown
export const VIEW_OPTIONS: { value: 'images' | 'list'; label: string; icon: string }[] = [
  { value: 'images', label: 'Images', icon: AssetUrls.icons.controls.images },
  { value: 'list', label: 'List', icon: AssetUrls.icons.controls.list },
];

// Get card image URL with fallback
export const getCardImageUrl = (item: CollectionList): string => {
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;

  if (item.card?.imageUris?.normal) return item.card.imageUris.normal;
  if (item.card?.imageUris?.small) return item.card.imageUris.small;
  if (item.card?.imageUris?.large) return item.card.imageUris.large;
  if (item.card?.normal_fallbacks?.[0]) return item.card.normal_fallbacks[0];
  if (item.card?.scryfallID) return AssetUrls.cards.getCardImage(item.card.scryfallID, 'normal');

  return defaultImage;
};

// Helper to get a valid condition value
export const getValidCondition = (condition?: string): string => {
  return condition && VALID_CONDITIONS.includes(condition) ? condition : 'NEAR_MINT';
};

// Helper to determine if card should show foil overlay
// Foil if acceptFoil=true AND acceptNonFoil=false (foil only)
// If "Any" (both true or both undefined), default to non-foil
export const shouldShowFoil = (item: CollectionList): boolean => {
  const acceptsFoil = item.acceptFoil ?? true;
  const acceptsNonFoil = item.acceptNonFoil ?? true;
  return acceptsFoil && !acceptsNonFoil;
};

// Helper to format conditions for list view display
export const formatConditions = (acceptableConditions?: string[]): string => {
  if (!acceptableConditions || acceptableConditions.length === 0) return 'Any';
  const conditionMap: Record<string, string> = {
    'NEAR_MINT': 'NM', 'SLIGHTLY_PLAYED': 'LP', 'MODERATELY_PLAYED': 'MP',
    'HEAVILY_PLAYED': 'HP', 'DAMAGED': 'DMG',
  };
  const mapped = acceptableConditions.map(cond => conditionMap[cond] || cond);
  if (mapped.length >= 4 && mapped.includes('NM') && mapped.includes('LP') && mapped.includes('MP') && mapped.includes('HP')) {
    return 'Any';
  }
  return mapped.join(', ');
};

// Helper to format foil preference for list view display
export const formatFoilPreference = (acceptFoil?: boolean, acceptNonFoil?: boolean): React.ReactNode => {
  if (acceptFoil && acceptNonFoil) return 'Any';
  if (acceptFoil) {
    return React.createElement('img', {
      src: `${AssetUrls.base}/assets/add-cards/foil.png`,
      width: 14.15,
      height: 14.21,
      alt: 'foil-icon',
    });
  }
  return '-';
};

